import React, { useEffect, useState } from "react";
import {
  Flex, Icon, Image, Text, useColorModeValue, Link, Button, IconButton,
  Tooltip, useClipboard, Select, Box, Checkbox, Stack, Badge,
  HStack, VStack
} from "@chakra-ui/react";
import Card from "components/card/Card.js";
import { StarIcon } from "@chakra-ui/icons";
import { MdCheckCircle, MdCancel, MdShoppingCart, MdContentCopy, MdDelete, MdAlbum, MdOutlineError, MdCalendarToday } from "react-icons/md";
import Amazon from 'assets/img/amazon.ico';
import Flipkart from 'assets/img/flipkart.ico';
import Snapdeal from 'assets/img/snapdeal.ico';
import Meesho from 'assets/img/meesho.ico';
import Glowroad from 'assets/img/glowroad.ico';
import Shopsy from 'assets/img/shopsy.ico';
import freeup from 'assets/img/freeup.ico';
import indiamart from 'assets/img/indiamart.ico';
import RelevanceBadge from 'components/badge/RelevanceBadge';

import imageNotFound from 'assets/img/Image_not_available.png';

// --- Internal Helper Components ---

const getStatusConfig = (val) => {
  const configs = {
    whiteList: { color: "green", icon: MdOutlineError, label: "WHITELIST" },
    rejected: { color: "green", icon: MdOutlineError, label: "REJECTED" },
    test: { color: "blue", icon: MdShoppingCart, label: "TEST PURCHASE" },
    blackList: { color: "red", icon: MdCheckCircle, label: "INFRINGEMENT" },
    delete: { color: "orange", icon: MdDelete, label: "REMOVE" },
  };
  return configs[val] || { color: "gray", icon: MdAlbum, label: "SELECT ACTION" };
};

const StatusOverlay = ({ toggles }) => {
  const activeAction = Object.keys(toggles).find((key) => toggles[key] === true);
  if (!activeAction) return null;

  const config = {
    whiteList: { icon: MdCheckCircle, color: "green.500", label: "WHITELIST" },
    rejected: { icon: MdCheckCircle, color: "green.500", label: "REJECTED" },
    test: { icon: MdShoppingCart, color: "blue.500", label: "TEST" },
    blackList: { icon: MdCancel, color: "red.500", label: "REPORT" },
    delete: { icon: MdDelete, color: "orange.500", label: "DELETE" },
  };

  const { icon, color, label } = config[activeAction];

  return (
    <Box
      position="absolute"
      top="0" left="0" right="0" bottom="0"
      bg="blackAlpha.700"
      borderRadius="xl"
      zIndex="2"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      transition="all 0.2s"
    >
      <Icon as={icon} color="white" w={10} h={10} mb={2} />
      <Text color="white" fontWeight="bold" fontSize="xs" letterSpacing="wider">
        {label}
      </Text>
    </Box>
  );
};

const getIcon = (iconTag, isActive, size = 20) => {
  const inactiveGray = "#A0AEC0"
  return (
    <Icon as={iconTag} color={isActive ? "white" : inactiveGray} boxSize={`${size}px`} />
  )
};

// --- New: Formats and displays the detection date consistently across both views ---
const DetectedDateBadge = ({ date, variant = "grid" }) => {
  if (!date) return null;

  const formatDate = (d) => {
    const parsed = new Date(d);
    if (isNaN(parsed.getTime())) return d; // fall back to raw value if unparsable
    return parsed.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  if (variant === "list") {
    return (
      <HStack spacing={1} mt={1}>
        <Icon as={MdCalendarToday} color="gray.400" boxSize="12px" />
        <Text fontSize="xs" color="gray.500">Detected: <b>{formatDate(date)}</b></Text>
      </HStack>
    );
  }

  return (
    <Badge
      colorScheme="purple"
      variant="subtle"
      fontSize="9px"
      px={2}
      py={1}
      borderRadius="md"
      display="flex"
      alignItems="center"
      gap="4px"
      w="fit-content"
    >
      <Icon as={MdCalendarToday} boxSize="10px" />
      Detected: {formatDate(date)}
    </Badge>
  );
};

const ActionButtons = ({ product, isAdmin, handlers, toggles, layout }) => {
  return (
    <Flex gap={2} align="center" m={1}>
      <Button variant="solid" colorScheme="red" width={150} gap={1} onClick={() => handlers.onBlackList(product)}>Infringement</Button>
      <Button variant="outline" colorScheme="green" gap={1} onClick={() => handlers.onWhiteList(product)}>Reject</Button>
      {layout == "list" && isAdmin && (
        <Tooltip label='Delete Product'>
          <IconButton
            size="md"
            variant={toggles.delete ? "solid" : "ghost"}
            colorScheme={toggles.delete ? "red" : "gray"}
            icon={getIcon(MdDelete, toggles.delete)}
            onClick={() => handlers.onDelete(product)}
          />
        </Tooltip>
      )}
    </Flex>
  );
};

// --- GridView with Overlay Fix ---
const GridView = ({ product, isSelected, onSelect, isAdmin, handlers, toggles, currentRelevance, handleRelevanceChange, common }) => {
  const { textColor, borderColor, getPlatformIcon, renderStars, onCopy, hasCopied } = common;
  const activeKey = Object.keys(toggles).find(k => toggles[k] === true);
  const statusInfo = getStatusConfig(activeKey);

  return (
    <Card
      p='5px'
      bg={useColorModeValue("white", "navy.800")}
      transition='0.3s cubic-bezier(.25,.8,.25,1)'
      _hover={{ shadow: "lg", transform: "translateY(-4px)" }}
      border={isSelected || activeKey ? "2px solid" : "1px solid"}
      borderColor={activeKey ? `${statusInfo.color}.500` : isSelected ? "brand.500" : borderColor}
      overflow='hidden' h="100%" display="flex" flexDirection="column" position="relative"
    >
      <Box position='relative' bg='white' p='15px' h='200px' borderBottom="1px solid" borderColor={borderColor} borderRadius="xl" overflow="hidden">
        {/* Correctly placed Overlay */}
        <StatusOverlay toggles={toggles} />

        <Link href={product.prod_link} isExternal w='100%' h='100%' display="block">
          <Image src={product.image_link} w='100%' h='100%' objectFit='contain' />
        </Link>
        <Box position='absolute' top='10px' left='10px' bg="white" borderRadius="md" p="1" shadow="sm" border="1px solid" borderColor="gray.100">
          <Image src={getPlatformIcon(product.platform_id)} w='18px' h='18px' />
        </Box>
        {isAdmin && (
          <Checkbox position="absolute" top="12px" right="12px" isChecked={isSelected} onChange={onSelect} colorScheme="brand" size="lg" zIndex="3" />
        )}
      </Box>
      <VStack px='15px' align='start' spacing='8px' flex='1' py={2}>
        <Link href={product.prod_link} isExternal w="100%">
          <Text color={textColor} fontWeight='700' fontSize='sm' noOfLines={2} h="40px">{product.title}</Text>
        </Link>
        <HStack w="100%" justify="space-between" align="center">
          <HStack spacing='1px' hidden={product.seller_name?.toLowerCase().startsWith("xxxx")}>{renderStars(product.overall_rating)}</HStack>
          <Box ml="auto">
            <RelevanceBadge
              value={currentRelevance}
              editable="true"
              onChange={handleRelevanceChange}
            />
          </Box>
        </HStack>
        <HStack w="100%" justify="space-between">
          <Text color={textColor} fontWeight='900' fontSize='xl' hidden={product.seller_name?.toLowerCase().startsWith("xxxx")}>₹{product.selling_price || "N/A"}</Text>
          <DetectedDateBadge date={product.date_detected} variant="grid" />

        </HStack>
        <VStack w='100%' align="start" spacing={1} bg={"gray.50"} p="2" borderRadius="md">
          <Text fontSize='13px' color="gray.500" noOfLines={1} hidden={product.seller_name?.toLowerCase().startsWith("xxxx")}>
            Seller: <b>{product.seller_name}</b>
          </Text>
          <HStack w="100%" justify="space-between">
            <Text fontSize='13px' color="gray.500">Code: <b>{product.prod_code}</b></Text>
            <IconButton size='xs' icon={<MdContentCopy />} variant='ghost' h="20px" aria-label="Copy to Clipboard"
              onClick={onCopy} />
          </HStack>
        </VStack>
      </VStack>
      <VStack spacing={0} w="100%" p={2}>
        <Select
          variant="unstyled" size="sm" textAlign="center" fontSize="xs" fontWeight="bold"
          p="2" border="1px solid" borderColor={"blue.200"} rounded={5}
          placeholder={product.abuse_type_id === 0 ? "NO ABUSE" : common.chooseAbuseType()}
          onChange={(e) => common.handleAbuseType(e.target.value)}
        >
          {common.abuseTypes?.map(item => <option key={item.id} value={item.id}>{item.abuse_type}</option>)}
        </Select>
        <ActionButtons product={product} handlers={handlers} toggles={toggles} isSelected={isSelected} isAdmin={isAdmin} />
      </VStack>
    </Card>
  );
};

// --- ListView ---
const ListView = ({ product, isSelected, onSelect, isAdmin, handlers, toggles, currentRelevance, handleRelevanceChange, common }) => {
  const { textColor, renderStars, getPlatformIcon, abuseTypes, handleAbuseType, onCopy, hasCopied } = common;
  return (
    <Card p="16px" mb="10px" position="relative">
      <StatusOverlay toggles={toggles} />
      <Flex align="center" gap={4}>
        <Checkbox isChecked={isSelected} onChange={onSelect} colorScheme="brand" />
        <StatusOverlay toggles={toggles} />

        <Image src={product.image_link} w="100px" h="100px" objectFit="contain" />
        <Box flex="1">
          <Link href={product.prod_link} isExternal fontWeight="bold" fontSize="md" color={textColor}>{product.title}</Link>
          <Flex align="center" mt={1}>
            <Image src={getPlatformIcon(product.platform_id)} w="15px" h="15px" mr={2} />
            <Text fontSize="sm" color="gray.500">{product.keyword}</Text>
          </Flex>
          <HStack><Text fontSize={14}>Code: {product.prod_code}</Text><IconButton size='xs' icon={<MdContentCopy />} onClick={onCopy} variant='ghost' /></HStack>
          <Text color={textColor} fontWeight='900' fontSize='xl' hidden={product.seller_name?.toLowerCase().startsWith("xxxx")}>₹{product.selling_price || "N/A"}</Text>
          <Text fontWeight="bold" fontSize={14} hidden={product.seller_name?.toLowerCase().startsWith("xxxx")}>Seller: {product.seller_name}</Text>
          <DetectedDateBadge date={product.date_detected} variant="list" />
          <RelevanceBadge
            value={currentRelevance}
            editable="true"
            onChange={handleRelevanceChange}
          />
        </Box>
        <Stack direction="row" spacing={4} align="center">
          <Select size="xs" w="130px" placeholder={product.abuse_type_id === 0 ? "NO ABUSE" : common.chooseAbuseType()} onChange={(e) => handleAbuseType(e.target.value)}>
            {/* <option value="0">None Abuse</option> */}
            {abuseTypes?.map(item => <option key={item.id} value={item.id}>{item.abuse_type}</option>)}
          </Select>
          <ActionButtons product={product} isAdmin={isAdmin} handlers={handlers} toggles={toggles} layout={"list"} />
        </Stack>
      </Flex>
    </Card>
  );
};

export default function SuspiciousItem(props) {
  const { product, handleInsertSeller, removeFromInsert, abuseTypes, handleInsertAbuse, handleInsertRelevance, setNoAbuseProducts } = props;
  const [toggles, setToggles] = useState({ whiteList: false, test: false, blackList: false, delete: false, rejected: false });
  const textColor = useColorModeValue("brands.900", "white");
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.100");
  const { hasCopied, onCopy } = useClipboard(product.prod_code);
  const [currentRelevance, setCurrentRelevance] = useState(
    product.relevance || "SELECT"
  );
  useEffect(() => {
    setCurrentRelevance(product.relevance || "SELECT");
  }, [product.relevance]);


  const handleAction = async (type) => {
    const keyMap = { 'W': 'whiteList', 'T': 'test', 'S': 'blackList', 'X': 'delete' };
    const key = keyMap[type];
    const active = !toggles[key];
    setToggles({ whiteList: false, test: false, blackList: false, delete: false, rejected: false, [key]: active });
    if (active) await handleInsertSeller(product, type);
    else await removeFromInsert(product, type);
  };
  const handleRelevanceChange = (relevance) => {

    setCurrentRelevance(relevance);

    handleInsertRelevance(product, relevance);

  };

  const commonProps = {
    textColor, borderColor, abuseTypes, onCopy,
    getPlatformIcon: (id) => [Amazon, Flipkart, Snapdeal, Meesho, Glowroad, Shopsy, freeup, indiamart][id - 1] || imageNotFound,
    renderStars: (rating) => {
      const val = rating < 0 ? 0 : rating;
      return Array.from({ length: 5 }, (_, i) => (
        <Icon as={StarIcon} key={i} color={i < Math.floor(val) ? "gold" : "gray.300"} />
      ));
    },
    chooseAbuseType: () => {
      const types = { 0: 'None', 10: 'None', 1: 'MRP', 9: 'MRP', 2: 'Price', 3: 'Manufacturing', 8: 'Manufacturing', 4: 'Brand', 7: 'Brand', 5: 'Image', 6: 'Counterfeit' };
      return types[product.abuse_type_id] ? `${types[product.abuse_type_id]} Abuse` : 'Choose Type';
    },
    handleAbuseType: async (abuse_type) => {
      if (abuse_type !== '') await handleInsertAbuse(product, parseInt(abuse_type));
      if (parseInt(abuse_type) === 0) {
        setNoAbuseProducts(prev => prev.some(p => p.prod_code === product.prod_code) ? prev : [product, ...prev]);
      } else {
        setNoAbuseProducts(prev => prev.filter(p => p.prod_code !== product.prod_code));
      }
    }
  };

  const handlers = {
    onWhiteList: () => handleAction('W'),
    onBlackList: () => handleAction('S'),
    onDelete: () => handleAction('X'),
    onReject: async (prod) => {
      setToggles({ whiteList: false, test: false, blackList: false, delete: false, rejected: true });
      if (prod.seller_name?.toLowerCase().startsWith("xxxx")) {
        await commonProps.handleAbuseType(0);
        handleAction('S')
      } else {
        await handleInsertSeller(prod, 'W');
      }
    }
  };

  return props.viewMode === 'grid'
    ? <GridView {...props} toggles={toggles} handlers={handlers} currentRelevance={currentRelevance}
      handleRelevanceChange={handleRelevanceChange} common={commonProps} />
    : <ListView {...props} toggles={toggles} handlers={handlers} currentRelevance={currentRelevance}
      handleRelevanceChange={handleRelevanceChange} common={commonProps} />;
}